import React, { useContext } from 'react';
import { 
  Box, 
  Flex, 
  Button, 
  Heading, 
  Spacer, 
  Image, 
  Skeleton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
  Avatar,
  VStack
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FiLogOut, FiUser } from 'react-icons/fi';
import AuthContext from '../context/AuthContext';
import FeatureUnlockContext from '../context/FeatureUnlockContext';
import finanseeLogo from '../assets/finanseeLogo.png';
import ThemeContext from '../context/ThemeContext';

const Navbar = () => {
  const { token, logout, user } = useContext(AuthContext);
  const { resetUnlocks } = useContext(FeatureUnlockContext);
  const { themeData, logo, businessName, loadingTheme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const handleLogout = () => {
    resetUnlocks();
    logout();
    navigate('/');
  };

  // Extract theme colors with fallbacks
  const primaryColor = themeData?.colors?.primary || '#2B6CB0';
  const secondaryColor = themeData?.colors?.secondary || '#2C5282';
  const textColor = themeData?.colors?.text || '#1A202C';

  // Determine if theme is dark (midnight)
  const isDarkTheme = themeData?.id === 'midnight';
  
  // Navbar text should be white for light themes, use theme text color for dark
  const navbarTextColor = isDarkTheme ? textColor : 'white';
  
  // Button colors with proper contrast
  const buttonTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
  const buttonBgColor = isDarkTheme ? secondaryColor : 'white';
  const buttonHoverBg = isDarkTheme ? '#4A5568' : 'gray.100';
  const buttonActiveBg = isDarkTheme ? '#2D3748' : 'gray.200';

  // Menu colors
  const menuBg = isDarkTheme ? '#2D3748' : 'white';
  const menuTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
  const menuHoverBg = isDarkTheme ? '#4A5568' : 'gray.50';
  const menuBorderColor = isDarkTheme ? '#4A5568' : 'gray.200';

  // Use custom logo if available, otherwise use default FinanSee logo
  const displayLogo = logo || finanseeLogo;
  const displayName = businessName || 'FinanSee';

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <Box
      bg={primaryColor}
      px={8}
      py={4}
      color={navbarTextColor}
      boxShadow="md"
      w="full"
    >
      <Flex align="center">
        <Flex align="center" gap={2}>
          {loadingTheme ? (
            <Skeleton 
              boxSize="40px" 
              borderRadius="md"
              startColor={secondaryColor}
              endColor={primaryColor}
            />
          ) : (
            <Image
              src={displayLogo}
              alt={`${displayName} Logo`}
              boxSize="40px"
              objectFit="contain"
              fallbackSrc={finanseeLogo}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = finanseeLogo;
              }}
            />
          )}
          {loadingTheme ? (
            <Skeleton 
              height="32px" 
              width="150px"
              startColor={secondaryColor}
              endColor={primaryColor}
            />
          ) : (
            <Heading
              as={Link}
              to="/"
              size="lg"
              cursor="pointer"
              _hover={{ textDecoration: 'none', opacity: 0.9 }}
            >
              {displayName}
            </Heading>
          )}
        </Flex>
        <Spacer />
        {token && user ? (
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              color={buttonTextColor}
              bg={buttonBgColor}
              size="md"
              px={4}
              _hover={{
                bg: buttonHoverBg,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              _active={{
                bg: buttonActiveBg,
                transform: 'translateY(0)'
              }}
              transition="all 0.2s"
              fontWeight="semibold"
              boxShadow="md"
            >
              <Flex align="center" gap={2}>
                <Avatar 
                  size="sm" 
                  name={user.name || user.email} 
                  bg={primaryColor}
                  color="white"
                  fontSize="xs"
                >
                  {getUserInitials()}
                </Avatar>
                <Text display={{ base: 'none', md: 'block' }}>
                  {user.name || 'Account'}
                </Text>
              </Flex>
            </MenuButton>
            <MenuList
              bg={menuBg}
              borderColor={menuBorderColor}
              boxShadow="lg"
              py={2}
            >
              <Box px={4} py={3}>
                <VStack align="start" spacing={1}>
                  <Flex align="center" gap={2} w="full">
                    <FiUser />
                    <Text fontSize="sm" fontWeight="semibold" color={menuTextColor}>
                      {user.name || 'User'}
                    </Text>
                  </Flex>
                  <Text fontSize="xs" color={isDarkTheme ? 'gray.400' : 'gray.500'}>
                    {user.email}
                  </Text>
                  <Text 
                    fontSize="xs" 
                    color={isDarkTheme ? 'blue.300' : 'blue.600'}
                    fontWeight="medium"
                    textTransform="capitalize"
                  >
                    {user.role}
                  </Text>
                </VStack>
              </Box>
              <MenuDivider borderColor={menuBorderColor} />
              <MenuItem
                icon={<FiLogOut />}
                onClick={handleLogout}
                color={isDarkTheme ? 'red.300' : 'red.600'}
                _hover={{ bg: menuHoverBg }}
                fontWeight="medium"
              >
                Logout
              </MenuItem>
            </MenuList>
          </Menu>
        ) : (
          <Button
            as={Link}
            to="/login"
            color={buttonTextColor}
            bg={buttonBgColor}
            size="md"
            px={6}
            _hover={{
              bg: buttonHoverBg,
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
            _active={{
              bg: buttonActiveBg,
              transform: 'translateY(0)'
            }}
            transition="all 0.2s"
            fontWeight="semibold"
            boxShadow="md"
          >
            Login
          </Button>
        )}
      </Flex>
    </Box>
  );
};

export default Navbar;